/* <hj-storm> — storm overlay for the HJ Graveyard footer photo.
   One canvas, one rAF loop: wind-blown rain with splashes, one calm lightning strike every 6.5s with a blue sky flash.
   Each strike also lights up the "REST IN PIECES" divider above the photo: a blue copy of it ([data-hj-rip]) sits on top
   of the white one, and this fades that copy in with the flash and back out, so the divider goes blue, then calms to white.

   Strike timeline (ms after the strike starts):
     bolt     0–70 full · 70–140 dim (.3) · 140–210 re-strike (.9) · 210–1100 afterglow · sky flash follows the bolt
     divider  0–40 to blue · 70–140 dips to .55 with the bolt · 140–210 back to full · holds to 900 · 900–3000 calms to white (smoothstep)

   Attributes (all optional, read live):
     rain="0.55"          amount of rain, 0–1 (0 = no rain)
     lightning="off"      turns lightning (and the divider glow) off
     every="6.5"          seconds between strikes (min 5)
     first="1.2"          seconds before the first strike
     zone="0.01,0.15"     bolt x range, fractions of this element's width
     reach="0.6"          where the bolt ends, fraction of this element's height (+0–2%)
     ground="0.72,0.88"   band where drops land and splash, fractions of height
     rip="[data-hj-rip]"  selector of the blue divider copy; looked up from the nearest ancestor that contains one
   Events:  "hj-strike" (bubbles) at every strike.
   QA:      el.strike() fires one now · el.still(ms) freezes the frame ms into a strike · el.play() resumes.
   Runs only while on screen and the tab is visible; DPR capped at 2. Reduce Motion: no rain, bolt or glow (divider stays white).
   Usage:   <hj-storm rain="0.55" every="6.5" style="position:absolute;top:0;left:0;width:100%;height:100%;display:block;pointer-events:none"></hj-storm>
            <div style="position:relative">REST IN PIECES <div data-hj-rip style="position:absolute;inset:0;opacity:0;color:#5A86F0">REST IN PIECES</div></div> */
(function () {
  if (typeof window === 'undefined' || !window.customElements || customElements.get('hj-storm')) return;

  function num(v, d) { v = parseFloat(v); return isFinite(v) ? v : d; }
  function pair(v, d) { var a = String(v == null ? '' : v).split(',').map(parseFloat); return a.length === 2 && isFinite(a[0]) && isFinite(a[1]) ? a : d; }

  function startStorm(canvas, getConf, hooks) {
    hooks = hooks || {};
    var ctx = canvas.getContext('2d');
    var api = { stop: function () {}, strike: function () {}, still: function () {}, play: function () {}, refresh: function () {} };
    if (!ctx) return api;
    var RM = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var R = Math.random, BANDS = [[0.22, 0.45], [0.45, 0.72], [0.72, 1.01]];
    var W = 0, H = 0, D = 1, k = 1, u = 1, MAX = 0, drops = [], splashes = [];
    var raf = 0, last = 0, clock = 0, alive = true, frozen = false, vis = !('IntersectionObserver' in window), io = null;
    var conf = read(), nextAt = conf.first, at = null, path = null, branches = [], flash = null, ripQ = -1;

    function read() {
      var c = (getConf && getConf()) || {};
      return {
        rain: Math.max(0, Math.min(1, num(c.rain, 0.55))),
        bolt: c.lightning !== false,
        every: Math.max(5, num(c.every, 6.5)) * 1000,
        first: Math.max(0, num(c.first, 1.2)) * 1000,
        zone: pair(c.zone, [0.01, 0.15]),
        reach: Math.max(0.1, Math.min(0.95, num(c.reach, 0.6))),
        ground: pair(c.ground, [0.72, 0.88])
      };
    }
    function reduced() { return !!(RM && RM.matches); }
    function setRip(q) { if (q === ripQ) return; ripQ = q; if (hooks.rip) hooks.rip(q); }

    function size() {
      var w = canvas.clientWidth, h = canvas.clientHeight, d = Math.min(2, window.devicePixelRatio || 1);
      if (!w || !h) return false;
      if (w === W && h === H && d === D) return true;
      W = w; H = h; D = d;
      canvas.width = Math.round(W * D); canvas.height = Math.round(H * D);
      ctx.setTransform(D, 0, 0, D, 0, 0);
      k = Math.max(1, Math.min(1.6, W / 402));          // stroke + streak scale
      u = Math.max(0.8, Math.min(1.4, H / 700));        // fall speed scale
      MAX = Math.round(Math.max(60, Math.min(520, 170 * W * H / (402 * 700))));
      drops = []; splashes = [];
      for (var i = 0; i < MAX; i++) drops.push(mkDrop({}, true));
      path = null; // bolt geometry is in px; drop it on resize
      return true;
    }
    function mkDrop(d, init) {
      var z = 0.22 + Math.pow(R(), 1.6) * 0.78, g = conf.ground;
      d.z = z; d.len = (7 + z * 20) * k; d.sp = (430 + z * 640) * u;
      d.x = R() * (W + H * 0.5) - H * 0.5;
      d.g = H * (g[0] + R() * (g[1] - g[0]));
      d.y = init ? R() * d.g : -20 - R() * 140;
      return d;
    }

    // midpoint displacement, x kept inside [a0, a1]
    function seg(x0, y0, x1, y1, d, a0, a1) {
      var a = [[x0, y0], [x1, y1]];
      while (d > 1.2) {
        var b = [a[0]];
        for (var i = 1; i < a.length; i++) {
          var p = a[i - 1], q = a[i];
          b.push([Math.max(a0, Math.min(a1, (p[0] + q[0]) / 2 + (R() - 0.5) * d)), (p[1] + q[1]) / 2 + (R() - 0.5) * d * 0.25], q);
        }
        a = b; d *= 0.55;
      }
      return a;
    }
    function mkBolt() {
      var lo = W * conf.zone[0], hi = W * conf.zone[1];
      if (hi - lo < 30) hi = lo + 30;
      var span = hi - lo, yEnd = H * conf.reach + R() * H * 0.02;
      path = seg(lo + span * (0.3 + R() * 0.4), -8, lo + span * (0.15 + R() * 0.5), yEnd, Math.max(46, span * 0.5), lo, hi);
      branches = [];
      for (var j = 0, nb = R() < 0.6 ? 2 : 1; j < nb; j++) {
        var st = path[Math.floor(path.length * (0.3 + R() * 0.3))], dir = R() < 0.5 ? -1 : 1;
        branches.push(seg(st[0], st[1], st[0] + dir * (18 + R() * 22) * k, st[1] + (50 + R() * 60) * k, 22, lo - 3, hi + 12));
      }
      flash = { x: (lo + hi) / 2, y: yEnd * 0.42, r: H * 0.43 };
    }
    function boltI(ph) { return ph < 0 ? 0 : ph < 70 ? 1 : ph < 140 ? 0.3 : ph < 210 ? 0.9 : ph < 1100 ? 0.9 * Math.exp(-(ph - 210) / 230) : 0; }
    // divider glow: on with the return stroke, flickers with the re-strike, holds, then calms back to white
    function glowAt(ph) {
      if (ph < 0) return 0;
      if (ph < 40) return ph / 40;
      if (ph < 70) return 1;
      if (ph < 140) return 0.55;
      if (ph < 900) return 1;
      if (ph < 3000) { var f = (ph - 900) / 2100; return 1 - f * f * (3 - 2 * f); }
      return 0;
    }
    function line(pts, w, ga) {
      ctx.globalAlpha = ga; ctx.lineWidth = w; ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
      for (var i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.stroke();
    }
    function drawBolt(b) {
      var i;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter'; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      ctx.shadowColor = 'rgba(100,155,255,' + b.toFixed(3) + ')'; ctx.shadowBlur = 18 * k * D;
      ctx.strokeStyle = 'rgba(120,170,255,' + (0.38 * b).toFixed(3) + ')';
      line(path, 5 * k, 1); for (i = 0; i < branches.length; i++) line(branches[i], 3 * k, 0.6);
      ctx.shadowBlur = 6 * k * D;
      ctx.strokeStyle = 'rgba(205,225,255,' + (0.85 * b).toFixed(3) + ')';
      line(path, 2 * k, 1); for (i = 0; i < branches.length; i++) line(branches[i], 1.2 * k, 0.6);
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'rgba(255,255,255,' + b.toFixed(3) + ')';
      line(path, k, 1);
      ctx.restore();
    }

    function draw(dt) {
      ctx.clearRect(0, 0, W, H);
      var ph = -1, i, d;
      if (conf.bolt) {
        if (clock >= nextAt) { at = clock; nextAt = clock + conf.every; mkBolt(); if (hooks.strike) hooks.strike(); }
        if (at !== null) ph = clock - at;
      }
      var bI = ph >= 0 && path ? boltI(ph) : 0;
      if (bI > 0.01 && flash) {
        var g = ctx.createRadialGradient(flash.x, flash.y, 0, flash.x, flash.y, flash.r);
        g.addColorStop(0, 'rgba(170,200,255,' + (0.2 * bI).toFixed(3) + ')');
        g.addColorStop(1, 'rgba(170,200,255,0)');
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      }
      setRip(ph >= 0 ? Math.round(glowAt(ph) * 100) / 100 : 0);

      var n = Math.round(MAX * conf.rain);
      if (n > 0) {
        var wind = 0.36 + 0.08 * Math.sin(clock / 2600) + 0.04 * Math.sin(clock / 830);
        ctx.lineCap = 'round';
        for (var b = 0; b < 3; b++) {
          var lo = BANDS[b][0], hi = BANDS[b][1];
          ctx.beginPath();
          for (i = 0; i < n; i++) { d = drops[i]; if (d.z < lo || d.z >= hi) continue; ctx.moveTo(d.x, d.y); ctx.lineTo(d.x - wind * d.len, d.y - d.len); }
          ctx.strokeStyle = 'rgba(12,16,26,' + (0.1 + b * 0.06) + ')'; ctx.lineWidth = 1.6 + b * 0.6; ctx.stroke();       // dark edge: reads on bright sky
          ctx.strokeStyle = 'rgba(226,233,246,' + (0.26 + b * 0.17) + ')'; ctx.lineWidth = 0.85 + b * 0.45; ctx.stroke();  // light core
        }
        ctx.beginPath(); ctx.strokeStyle = 'rgba(226,232,244,.45)'; ctx.lineWidth = 0.7;
        for (i = splashes.length - 1; i >= 0; i--) {
          var s = splashes[i]; s.t += dt;
          var p = s.t / 0.26;
          if (p >= 1) { splashes.splice(i, 1); continue; }
          var r = (1.5 + p * 6) * k;
          ctx.moveTo(s.x - r * 0.4, s.y - r * 0.15); ctx.lineTo(s.x - r, s.y - r * 0.7 * (1 - p));
          ctx.moveTo(s.x + r * 0.4, s.y - r * 0.15); ctx.lineTo(s.x + r, s.y - r * 0.7 * (1 - p));
        }
        ctx.stroke();
        for (i = 0; i < n; i++) {
          d = drops[i]; d.y += d.sp * dt; d.x += d.sp * wind * dt;
          if (d.y > d.g) { if (d.z > 0.7 && splashes.length < 40) splashes.push({ x: d.x, y: d.g, t: 0 }); mkDrop(d, false); }
        }
      }
      if (bI > 0.01 && path) drawBolt(bI);
    }

    function frame(t) {
      raf = 0;
      if (!alive) return;
      if (!size()) { raf = requestAnimationFrame(frame); return; }
      var dt = last ? Math.min(0.05, Math.max(0, (t - last) / 1000)) : 0.016; last = t;
      clock += dt * 1000; // own clock: pauses with the loop, so a strike never jumps while off screen
      draw(dt);
      raf = requestAnimationFrame(frame);
    }
    function clearAll() { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.setTransform(D, 0, 0, D, 0, 0); }
    function run() {
      var red = reduced();
      var go = alive && vis && !document.hidden && !red && !frozen && (conf.rain > 0 || conf.bolt);
      if (go && !raf) { last = 0; raf = requestAnimationFrame(frame); }
      else if (!go && raf) { cancelAnimationFrame(raf); raf = 0; }
      if (frozen) return; // QA freeze-frame: leave canvas and divider as drawn
      if (red) { clearAll(); setRip(0); }
      else if (!conf.bolt) { setRip(0); at = null; }
      if (!go && !red && !(conf.rain > 0 || conf.bolt)) clearAll();
    }

    if (!vis) { io = new IntersectionObserver(function (es) { vis = es[es.length - 1].isIntersecting; run(); }, { rootMargin: '120px 0px' }); io.observe(canvas); }
    document.addEventListener('visibilitychange', run);
    if (RM) { if (RM.addEventListener) RM.addEventListener('change', run); else if (RM.addListener) RM.addListener(run); }
    run();

    api.refresh = function () {
      var was = conf.every, wasBolt = conf.bolt; conf = read();
      if (conf.bolt && !wasBolt) nextAt = clock + conf.first;
      else if (at !== null && conf.every !== was) nextAt = at + conf.every;
      run();
    };
    api.strike = function () {
      if (!alive || !size()) return;
      conf.bolt = true; frozen = false;
      at = clock; nextAt = clock + conf.every; mkBolt(); if (hooks.strike) hooks.strike();
      run();
    };
    api.still = function (ms) {
      if (!alive || !size()) return;
      frozen = true; run();
      at = clock - Math.max(0, +ms || 0); nextAt = clock + conf.every; mkBolt();
      var b = conf.bolt; conf.bolt = true; draw(0); conf.bolt = b;
    };
    api.play = function () { frozen = false; run(); };
    api.stop = function () {
      alive = false; cancelAnimationFrame(raf); raf = 0;
      if (io) io.disconnect(); io = null;
      document.removeEventListener('visibilitychange', run);
      if (RM) { if (RM.removeEventListener) RM.removeEventListener('change', run); else if (RM.removeListener) RM.removeListener(run); }
      clearAll(); setRip(0);
    };
    return api;
  }

  class HjStorm extends HTMLElement {
    static get observedAttributes() { return ['rain', 'lightning', 'every', 'first', 'zone', 'reach', 'ground', 'rip']; }
    connectedCallback() {
      if (this._api) return;
      var self = this, c = document.createElement('canvas');
      c.setAttribute('aria-hidden', 'true');
      c.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;display:block;pointer-events:none';
      if (getComputedStyle(this).position === 'static') this.style.position = 'relative';
      this.insertBefore(c, this.firstChild);
      this._c = c;
      this._api = startStorm(c, function () { return self._conf(); }, {
        rip: function (q) { var el = self._ripEl(); if (el) el.style.opacity = String(q); },
        strike: function () { self.dispatchEvent(new CustomEvent('hj-strike', { bubbles: true })); }
      });
    }
    disconnectedCallback() {
      if (this._api) this._api.stop();
      this._api = null;
      if (this._c && this._c.parentNode) this._c.parentNode.removeChild(this._c);
      var el = this._ripEl(); if (el) el.style.opacity = '0';
      this._c = null; this._rip = null;
    }
    attributeChangedCallback(name) { if (name === 'rip') { this._rip = null; this._ripMiss = 0; } if (this._api) this._api.refresh(); }
    _conf() {
      var a = this.getAttribute('lightning');
      return { rain: this.getAttribute('rain'), lightning: !(a === 'off' || a === 'false'), every: this.getAttribute('every'), first: this.getAttribute('first'), zone: this.getAttribute('zone'), reach: this.getAttribute('reach'), ground: this.getAttribute('ground') };
    }
    _ripEl() {
      if (this._rip && this._rip.isConnected) return this._rip;
      this._rip = null;
      var now = Date.now();
      if (this._ripMiss && now - this._ripMiss < 1000) return null; // none found: look again at most once a second
      var sel = this.getAttribute('rip') || '[data-hj-rip]';
      try { for (var p = this.parentElement; p; p = p.parentElement) { var e = p.querySelector(sel); if (e) { this._rip = e; break; } } } catch (x) {}
      this._ripMiss = this._rip ? 0 : now;
      return this._rip;
    }
    strike() { if (this._api) this._api.strike(); }
    still(ms) { if (this._api) this._api.still(ms); }
    play() { if (this._api) this._api.play(); }
  }
  window.hjStartStorm = startStorm;
  customElements.define('hj-storm', HjStorm);
})();
